/**
 * dtmf.js contains one call operation:
 *    - sendDtmf: Send DTMF tone(s) over a call.
 *
 * and several "helper" functions for it:
 *    - playInBand: Functionality for in-band DTMF.
 *    - playOutBand: Functionality for out-of-band DTMF.
 *      - addTones: Generates DTMF tones and adds them to the audio.
 *    - hasTelephoneEvent: Helper to determine out-of-band DTMF support.
 *    - convertTone, splitTones: Helpers for parsing input.
 */

// Call plugin.
import { callActions } from '../../call/interfaceNew/actions'
import { getCallById } from '../../call/interfaceNew/selectors'
import { CALL_STATES } from '../../call/constants'

// Other plugins.
import { logManager } from '../../logs'
import BasicError, { callCodes } from '../../errors'

// Libraries.
import { call, delay, put, select } from 'redux-saga/effects'
import sdpTransform from 'sdp-transform'

const log = logManager.getLogger('CALL')

/**
 * Entry saga for sending DTMF tones on a call.
 *
 * Determines if the DTMF tones need to be out-of-band (preferred) or in-band.
 *    Refers to helper functions to send the tones.
 *
 * Assumptions:
 *    1. None; everything is checked.
 * Responsibilities:
 *    1. Validate the operation is currently possible.
 *    2. Determine if out-of-band tones are possible for the call.
 *    3. Send the tones, depending on method available.
 * @method sendDtmf
 * @param {Object} deps        Dependencies that the saga uses.
 * @param {Object} deps.webRTC The WebRTC stack.
 * @param {Object} action      A "send dtmf" action.
 */
export function * sendDtmf (deps, action) {
  const { webRTC } = deps

  // Get the call.
  const targetCall = yield select(getCallById, action.payload.id)

  let error
  /**
   * Operation requirements:
   *    1. The call exists.
   *    2. The call is either Connected, Ringing, or Early Media.
   *    3. A valid tone was provided.
   */
  if (!targetCall) {
    log.info(`Failed to send DTMF tones on unknown call ${action.payload.id}`)
    error = new BasicError({
      code: callCodes.INVALID_PARAM,
      message: 'Call state not found; invalid call ID.'
    })
  } else if (![CALL_STATES.CONNECTED, CALL_STATES.RINGING, CALL_STATES.EARLY_MEDIA].includes(targetCall.state)) {
    log.info(`Failed to send DTMF tones on call ${targetCall.id}: Bad call state.`)
    error = new BasicError({
      code: callCodes.INVALID_STATE,
      message: `Call is in an invalid state (${targetCall.state}).`
    })
  } else if (!action.payload.tone) {
    log.info(`Failed to send DTMF tones on call ${targetCall.id}: Invalid tone.`)
    error = new BasicError({
      code: callCodes.INVALID_PARAM,
      message: 'No DTMF tone specified.'
    })
  }

  if (error) {
    // Stop the operation if it cannot be performed right now.
    yield put(callActions.sendDTMFFinish(action.payload.id, { error }))
    return
  }

  // Get the webrtc Session for the call.
  const session = yield call([webRTC.sessionManager, 'get'], targetCall.webrtcSessionId)
  // Get the remote description for the Session.
  const remoteDesc = yield call([session, 'getRemoteDescription'])

  // TODO: Is this the correct SDP to use?
  const canSendOutBand = yield call(hasTelephoneEvent, remoteDesc.sdp)

  let result
  if (canSendOutBand) {
    log.debug(`Sending DTMF tones out-of-band for call ${targetCall.id}.`)
    result = yield call(playOutBand, session, action.payload)
  } else {
    log.debug(`Sending DTMF tones in-band for call ${targetCall.id}.`)
    result = yield call(playInBand, session, action.payload)
  }

  if (result) {
    log.info(`Successfully sent DTMF tones for call ${targetCall.id}.`)
    yield put(callActions.sendDTMFFinish(action.payload.id))
  } else {
    log.debug(`Failed to send DTMF tones for call ${targetCall.id}.`)
    yield put(
      callActions.sendDTMFFinish(action.payload.id, {
        error: new BasicError({
          code: callCodes.GENERIC_ERROR,
          message: `Unable to send DTMF tones for call ${targetCall.id}.`
        })
      })
    )
  }
}

/**
 * Sends DTMF tones out-of-band over a Session.
 * This saga defines how out-of-band tones are sent over a call.
 *
 * The Callstack implementation uses the WebRTC stack's implementation for this.
 *    There is no signaling involved.
 *
 * @method playOutBand
 * @param  {Session} session A Webrtc Session object.
 * @param  {Object}  options
 * @param  {string}  options.tone           The tone sequence.
 * @param  {number}  [options.duration]     The length of each DTMF tone.
 * @param  {number}  [options.intertoneGap] The gap between each DTMF tone.
 * @return {boolean} Whether the operation was successful or not.
 */
export function * playOutBand (session, options) {
  const result = yield call([session, 'sendDTMF'], options)
  return result
}

/**
 * Sends DTMF tones in-band over a Session.
 * This saga defines how in-band tones are sent over a call.
 *
 * DTMF tones are added to the audio track by using AudioContext nodes. A
 *    MediaStream is converted into an audio node, which allows us to add more
 *    audio inputs into the node. The original audio and DTMF tones are mixed
 *    into a single node this way, and the track from this node temporarily
 *    replaces the original track in the Session.
 *
 *                       +-------------------------+
 *    Original           | Additional Audio Source |
 *   MediaStream         +---------+---------------+
 *       |                         |
 *       |                         |
 *       v                         v
 * +-----+-------+         +-------+----------+
 * | Source Node +-------->| Destination Node |
 * +-------------+         +-------+----------+
 *                                 |
 *                                 |
 *                                 v
 *                         MediaStream w/ DTMF
 *
 * @method playInBand
 * @param  {Session} session A Webrtc Session object.
 * @param  {Object}  options
 * @param  {string}  options.tone           The tone sequence.
 * @param  {number}  [options.duration=100]     The length of each DTMF tone.
 * @param  {number}  [options.intertoneGap=70]  The gap between each DTMF tone.
 * @return {boolean} Whether the operation was successful or not.
 */
export function * playInBand (session, options) {
  let { tone, duration, intertoneGap: gap } = options

  // Ensure optional options are numbers (not strings). If not numbers, or
  //    undefined, use default values.
  duration = Number(duration) > 0 ? Number(duration) : 100
  gap = Number(gap) > -1 ? Number(gap) : 70

  // Get the (native) track the tones will be inserted into.
  let track
  for (const localTrack of session.localTracks) {
    // Matching the track is done this way because we need to yield to getState,
    //    but we can't yield inside of a `.find(func)` or similar. So iterate
    //    over the new tracks "manually".
    const state = yield call([localTrack, 'getState'])
    if (state.kind === 'audio') {
      track = localTrack
      break
    }
  }

  const stream = yield call([track, 'getStream'])
  const context = new AudioContext()

  // Create a source node that has the stream media.
  const source = context.createMediaStreamSource(stream)

  /**
   * Create the "destination node". All audio sources will be connected to This
   *    node, and it will be used as the "final output".
   */
  const dest = context.createMediaStreamDestination()
  // Connect the source node to it, so that it has the original audio.
  source.connect(dest)

  // Get the track from the destination node and replace the original track
  //    on the PeerConnection with it. The remote endpoint will now receive
  //    any/all audio that is connected to the destination node.
  const newTrack = dest.stream.getAudioTracks()[0]
  yield call([session.peer, 'replaceTrack'], newTrack, { trackId: track.id })

  // Add the DTMF tones to the destination node.
  yield call(addTones, context, dest, tone, { duration, gap })

  // Un-replace track on the PeerConnection. This resets the tracks back to
  //    "normal".
  yield call([session.peer, 'replaceTrack'], track.track, { trackId: newTrack.id })

  // Clean-up the AudioContext nodes.
  context.close()

  return true
}

/**
 * Generates DTMF tones based on input, and connects the audio to the provided
 *    target node.
 * @method addTones
 * @param  {AudioContext} context      The AudioContext being used.
 * @param  {AudioNode} targetNode      The AudioNode to add DTMF tones to.
 * @param  {string} tones              The input tones.
 * @param  {Object} [options]
 * @param  {number} [options.duration] The length of each DTMF tone.
 * @param  {number} [options.gap]      The gap between each DTMF tone.
 */
function * addTones (context, targetNode, tones, options) {
  /**
   * Node Diagram:
   * +--------------+
   * | Oscillator 1 +--+
   * +--------------+  |    +--------------+
   *                   +--->|   Osc Node   |
   * +--------------+  |    +------+-------+
   * | Oscillator 2 +--+           |
   * +--------------+              | Connect generated audio
   *                               v to the provided node.
   *                        +------+--------+
   *                        |  Target Node  |
   *                        +---------------+
   */

  /**
   * Step 1: Setup the audio nodes.
   */
  // Create the Oscillator nodes that will generate the frequencies.
  const oscillators = [context.createOscillator(), context.createOscillator()]
  oscillators[0].type = 'sine'
  oscillators[1].type = 'sine'

  /**
   * Create an intermediary Gain node to control the volume of the oscillator
   *   frequencies before connecting them to anything else.
   */
  const oscNode = context.createGain()
  oscillators[0].connect(oscNode)
  oscillators[1].connect(oscNode)
  oscNode.gain.value = 0.1

  // Connect the combined oscillators to the target node. Any tones created
  //    by the oscillators will be heard in the target node.
  oscNode.connect(targetNode)

  // Connect the oscillators to the "destination" as well, which works magically.
  // `context.destination` connects to the "default destination" (or something
  //    like that), which will be the machine's speakers. This will play the
  //    audio locally.
  oscNode.connect(context.destination)

  /**
   * Step 2: Use the oscillators to generate DTMF tones.
   */

  /**
   * Schedule a frequency to be played for a period of time.
   * @method scheduleTone
   * @param  {number} tone  A frequency.
   * @param  {number} start When the tone should start.
   * @param  {number} dur   The duration of the tone, in seconds.
   */
  function scheduleTone (tone, start, dur) {
    oscillators[0].frequency.setValueAtTime(tone[0], start)
    oscillators[0].frequency.setValueAtTime(0, start + dur)

    oscillators[1].frequency.setValueAtTime(tone[1], start)
    oscillators[1].frequency.setValueAtTime(0, start + dur)
  }

  const toneSequence = splitTones(tones).map(convertTone)

  // Convert the time units to seconds.
  const dur = options.duration / 1000
  const gap = options.gap / 1000

  let toneStart = context.currentTime
  for (let i = 0; i < toneSequence.length; i++) {
    scheduleTone(toneSequence[i], toneStart, dur)

    toneStart = toneStart + dur + gap
  }

  // Start playing the scheduled tones.
  oscillators[0].start()
  oscillators[1].start()

  /**
   * Step 3: Clean-up the audio nodes.
   */

  // Wait until all tones have finished playing.
  const numTones = toneSequence.length
  const delayTime = numTones * (options.duration + options.gap)
  yield delay(delayTime)

  // Disconnect all nodes from each other.
  oscNode.disconnect()
  oscillators[0].stop()
  oscillators[0].disconnect()
  oscillators[1].stop()
  oscillators[1].disconnect()
}

/**
 * Checks whether an SDP has at least one telephone-event codec.
 *
 * TODO: If there isn't a telephone-event, then the PeerConnection won't have
 *    a "dtmf sender" (RTCRtpSender.dtmf). Should preferably check for that
 *    rather than looking through the SDP.
 * This isn't being done because the Session/Peer models wrap "send dtmf" in a
 *    way that makes it inconvenient to use.
 *
 * @method hasTelephoneEvent
 * @param  {string}  stringSdp An SDP.
 * @return {boolean}
 */
export function hasTelephoneEvent (stringSdp) {
  const sdp = sdpTransform.parse(stringSdp)

  const hasCodec = sdp.media
    .filter(media => media.type === 'audio')
    .some(media => {
      return media.rtp.some(rtp => rtp.codec === 'telephone-event')
    })

  return hasCodec
}

/**
 * Converts a single tone input to its dual-frequencies.
 * @method convertTone
 * @param  {string} tone A DTMF tone input character.
 * @return {Array} The set of frequencies for the DTMF tone.
 */
export function convertTone (tone) {
  if (typeof tone !== 'string' || tone.length > 1) {
    throw new Error('Invalid input for DTMF tone.')
  }

  switch (tone) {
    case '1':
      return ['697', '1209']
    case '2':
      return ['697', '1336']
    case '3':
      return ['697', '1477']
    case '4':
      return ['770', '1209']
    case '5':
      return ['770', '1336']
    case '6':
      return ['770', '1477']
    case '7':
      return ['852', '1209']
    case '8':
      return ['852', '1336']
    case '9':
      return ['852', '1477']
    case '0':
      return ['941', '1336']
    case '*':
      return ['941', '1209']
    case '#':
      return ['941', '1477']
  }
}

/**
 * Splits a DTMF tone sequence into individual tones.
 * @method splitTones
 * @param  {string} tone A DTMF tone string.
 * @return {Array} List of single-character tones.
 */
export function splitTones (tone) {
  const matches = tone.match(/[\d*#]+/)
  if (!matches || matches[0] !== tone) {
    throw new Error('Invalid characters in tone.')
  }

  return tone.split('')
}
